import { ApolloClient, from, InMemoryCache, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import cookies from 'js-cookie';
import { createUploadLink } from 'apollo-upload-client';
import ROUTES from '../constants/routes';

const graphqlLink = `${process.env.REACT_APP_API_URL}/graphql`;

const httpLink = createUploadLink({
  uri: graphqlLink,
  fetchOptions: {
    credentials: 'include',
  },
  credentials: 'include',
  headers: { 'Apollo-Require-Preflight': 'true' },
});

const authMiddleware = setContext(async (_, { headers = {} }) => {
  const token = cookies.get('accessToken');

  return {
    headers: {
      ...headers,
      authorization: token ? `JWT ${token}` : '',
    },
  };
});

let isRefreshing = false;
let failedRequestsQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = async (error: unknown, token: string | null = null) => {
  failedRequestsQueue.forEach((prom) => (error ? prom.reject(error) : prom.resolve(token)));
  failedRequestsQueue = [];
  isRefreshing = false;
};

const refreshAccessToken = async () => {
  const accessToken = cookies.get('accessToken');
  const refreshToken = cookies.get('refreshToken');

  if (!accessToken || !refreshToken) {
    return null;
  }
  const response = await fetch(graphqlLink, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `JWT ${accessToken}`,
    },
    body: JSON.stringify({
      query: `mutation refreshTokens {refreshTokens(refreshToken: "${refreshToken}") {
                                            accessToken
                                            refreshToken
                                        }}`,
    }),
  });

  if (!response.ok) {
    throw new Error('Ошибка при обновлении токена');
  }

  return response.json();
};

// eslint-disable-next-line consistent-return
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ extensions, path }) => {
      console.log(`[GraphQL error]: Message: ${extensions?.message}, Path: ${path}`);
    });
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }

  const unauthenticatedError = graphQLErrors?.find(
    ({ extensions }) => extensions?.code === 'UNAUTHENTICATED',
  );
  // eslint-disable-next-line
  const isLoginPage = location.pathname === `/${ROUTES.LOGIN}`;

  if (unauthenticatedError && !isLoginPage) {
    return new Observable((observer) => {
      new Promise<string | null>((resolve, reject) => {
        failedRequestsQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (token) {
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            };
            forward(operation).subscribe(subscriber);
          }
        })
        .catch(() => {
          // Обработка неудачного обновления токенов
        });

      if (!isRefreshing) {
        isRefreshing = true;
        refreshAccessToken()
          .then(async (data) => {
            if (data?.errors?.length) {
              throw new Error('Ошибка обновления токена');
            }

            const requestData = data.data.refreshTokens || data.data.loginByTelegramToken;
            const { accessToken, refreshToken } = requestData;
            cookies.set('accessToken', accessToken);
            cookies.set('refreshToken', refreshToken);

            await processQueue(null, accessToken);
          })
          .catch(async (e) => {
            console.log('error', e);
            await processQueue(e, null);

            cookies.remove('accessToken');
            cookies.remove('refreshToken');

            window.location.href = `/${ROUTES.LOGIN}?redirectUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`;
          });
      }
    });
  }
});

const client = new ApolloClient({
  link: from([errorLink, authMiddleware, httpLink]),
  cache: new InMemoryCache(),
});

export default client;
